import { EyeInvisibleOutlined, EyeOutlined, LockOutlined } from '@ant-design/icons';
import { Form, Input } from 'antd';
import { useState } from 'react';
import type { Control, FieldPath, FieldValues, RegisterOptions } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { BRAND } from '@/constants/brand';
import styles from './AuthForm.module.scss';

interface PasswordFieldProps<T extends FieldValues> {
  control: Control<T>;
  name: FieldPath<T>;
  label: string;
  rules?: RegisterOptions<T, FieldPath<T>>;
  placeholder?: string;
  size?: 'large' | 'middle' | 'small';
}

export function PasswordField<T extends FieldValues>({
  control,
  name,
  label,
  rules,
  placeholder = '••••••••',
  size = 'large',
}: PasswordFieldProps<T>) {
  const [visible, setVisible] = useState(false);

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState }) => (
        <Form.Item
          label={label}
          required
          validateStatus={fieldState.error ? 'error' : ''}
          help={fieldState.error?.message}
        >
          <Input
            {...field}
            type={visible ? 'text' : 'password'}
            size={size}
            status={fieldState.error ? 'error' : undefined}
            prefix={<LockOutlined style={{ color: BRAND.primary, fontSize: 15 }} />}
            suffix={
              <button
                type="button"
                tabIndex={-1}
                className={styles.passwordToggle}
                aria-label={visible ? 'Hide password' : 'Show password'}
                onClick={() => setVisible((value) => !value)}
              >
                {visible ? (
                  <EyeOutlined style={{ color: BRAND.placeholder, fontSize: 16 }} />
                ) : (
                  <EyeInvisibleOutlined style={{ color: BRAND.placeholder, fontSize: 16 }} />
                )}
              </button>
            }
            placeholder={placeholder}
            autoComplete="current-password"
          />
        </Form.Item>
      )}
    />
  );
}
